import { AppointmentProposalUpgrade } from './appointmentProposalUpgrades.models';

export interface AppointmentProposalUpgradesState {
  ids: string[];
  entities: Record<string, AppointmentProposalUpgrade>;
  selectedId: string | null;
  selectedProposalId: string | null;
  error: string | null;
  isLoaded: boolean;
  isLoading: boolean;
  loadedProposalIds: string[];
}

export const initState: AppointmentProposalUpgradesState = {
  ids: [],
  entities: {},
  selectedId: null,
  selectedProposalId: null,
  error: null,
  isLoaded: false,
  isLoading: true,
  loadedProposalIds: [],
};

type Action =
  | {
      type: 'FETCH_APPOINTMENT_PROPOSAL_UPGRADES_PRODUCTS';
      payload: { appointmentProposalId: string };
    }
  | {
      type: 'FETCH_APPOINTMENT_PROPOSAL_UPGRADES_PRODUCTS_SUCCESS';
      payload: {
        appointmentProposalUpgrades: AppointmentProposalUpgrade[];
        appointmentProposalId: string;
      };
    }
  | {
      type: 'FETCH_APPOINTMENT_PROPOSAL_UPGRADES_PRODUCTS_FAILURE';
      payload: { error: string };
    }
  | { type: 'CREATE_APPOINTMENT_PROPOSAL_UPGRADE' }
  | {
      type: 'CREATE_APPOINTMENT_PROPOSAL_UPGRADE_SUCCESS';
      payload: { appointmentProposalUpgrade: AppointmentProposalUpgrade };
    }
  | {
      type: 'CREATE_APPOINTMENT_PROPOSAL_UPGRADE_FAILURE';
      payload: { error: string };
    }
  | {
      type: 'DELETE_APPOINTMENT_PROPOSAL_UPGRADE';
      payload: { appointmentProposalUpgradeId: string };
    }
  | {
      type: 'DELETE_APPOINTMENT_PROPOSAL_UPGRADE_SUCCESS';
      payload: { appointmentProposalUpgradeId: string };
    }
  | {
      type: 'DELETE_APPOINTMENT_PROPOSAL_UPGRADE_FAILURE';
      payload: { error: string };
    }
  | {
      type: 'SET_LOADING_STATE';
      payload: { isLoading: boolean; isLoaded: boolean };
    };

export const appointmentProposalUpgradesReducer = (
  state: AppointmentProposalUpgradesState,
  action: Action
): AppointmentProposalUpgradesState => {
  switch (action.type) {
    case 'FETCH_APPOINTMENT_PROPOSAL_UPGRADES_PRODUCTS':
      return {
        ...state,
        error: null,
        isLoading: true,
        isLoaded: state.loadedProposalIds.includes(
          action.payload.appointmentProposalId
        ),
        selectedProposalId: action.payload.appointmentProposalId,
      };

    case 'FETCH_APPOINTMENT_PROPOSAL_UPGRADES_PRODUCTS_SUCCESS': {
      const fetchedUpgrades = action.payload.appointmentProposalUpgrades;
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
        loadedProposalIds: [
          ...new Set([
            ...state.loadedProposalIds,
            action.payload.appointmentProposalId,
          ]),
        ],
        ids: [
          ...new Set([
            ...state.ids,
            ...fetchedUpgrades.map((u) => u.appointmentProposalUpgradeId),
          ]),
        ],
        entities: fetchedUpgrades.reduce(
          (prev, curr) => ({
            ...prev,
            [curr.appointmentProposalUpgradeId]: curr,
          }),
          state.entities
        ),
      };
    }

    case 'CREATE_APPOINTMENT_PROPOSAL_UPGRADE':
      return {
        ...state,
        error: null,
        isLoading: true,
      };

    case 'CREATE_APPOINTMENT_PROPOSAL_UPGRADE_SUCCESS':
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
        ids: [
          ...new Set([
            ...state.ids,
            action.payload.appointmentProposalUpgrade
              .appointmentProposalUpgradeId,
          ]),
        ],
        entities: {
          ...state.entities,
          [action.payload.appointmentProposalUpgrade
            .appointmentProposalUpgradeId]:
            action.payload.appointmentProposalUpgrade,
        },
      };

    case 'DELETE_APPOINTMENT_PROPOSAL_UPGRADE':
      return {
        ...state,
        error: null,
        isLoading: true,
      };

    case 'DELETE_APPOINTMENT_PROPOSAL_UPGRADE_SUCCESS':
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
        ids: state.ids.filter(
          (id) => id !== action.payload.appointmentProposalUpgradeId
        ),
        entities: Object.fromEntries(
          Object.entries(state.entities).filter(
            ([key]) => key !== action.payload.appointmentProposalUpgradeId
          )
        ),
      };

    case 'FETCH_APPOINTMENT_PROPOSAL_UPGRADES_PRODUCTS_FAILURE':
    case 'CREATE_APPOINTMENT_PROPOSAL_UPGRADE_FAILURE':
    case 'DELETE_APPOINTMENT_PROPOSAL_UPGRADE_FAILURE':
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
        error: action.payload.error,
      };

    case 'SET_LOADING_STATE':
      return { ...state, ...action.payload };

    default:
      return state;
  }
};
